import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminPatrolSheetMaterial, UpsertAdminPatrolSheetMaterialBody } from "./types/patrolSheetMaterials";

export const usePatrolSheetMaterialsApi = () => {
  const api = useApi();

  const getAdminPatrolSheetMaterial = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/sheet_materials`;
      return useSWRMutation<AdminPatrolSheetMaterial, CustomError, string, string>(
        url,
        (url, { arg }: { arg: string }) => api.get<AdminPatrolSheetMaterial>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminPatrolSheetMaterials = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/sheet_materials", query);
      return useSWR<Paginated<AdminPatrolSheetMaterial>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminPatrolSheetMaterial>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminPatrolSheetMaterial = useCallback(
    async (body: UpsertAdminPatrolSheetMaterialBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/sheet_materials`;
      const id = await api.post<{ id: string }, UpsertAdminPatrolSheetMaterialBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminPatrolSheetMaterial = useCallback(
    async (sheetMaterialId: string, body: UpsertAdminPatrolSheetMaterialBody): Promise<void> => {
      const url = `/admin/settings/patrol/sheet_materials/${sheetMaterialId}`;
      (await api.put<UpsertAdminPatrolSheetMaterialBody>(url, body),
        mutateCache(["/admin/settings/patrol/sheet_materials"]));
    },
    [api],
  );

  const deleteAdminPatrolSheetMaterial = useCallback(
    async (sheetMaterialId: string): Promise<void> => {
      const url = `/admin/settings/patrol/sheet_materials/${sheetMaterialId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/sheet_materials`]);
    },
    [api],
  );

  const updateAdminPatrolSheetMaterialVisibility = useCallback(
    async (sheetMaterialId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/sheet_materials/${sheetMaterialId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/sheet_materials"]);
    },
    [api],
  );

  const updateAdminPatrolSheetMaterialOrder = useCallback(
    async (sheetMaterialId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/sheet_materials/${sheetMaterialId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/sheet_materials"]);
    },
    [api],
  );

  return {
    getAdminPatrolSheetMaterial,
    useAdminPatrolSheetMaterials,
    insertAdminPatrolSheetMaterial,
    updateAdminPatrolSheetMaterial,
    updateAdminPatrolSheetMaterialVisibility,
    updateAdminPatrolSheetMaterialOrder,
    deleteAdminPatrolSheetMaterial,
  };
};
